import { supabase } from '@/lib/supabase';
import { callGeminiImage } from '@/lib/gemini';
import { validatePersonaUpdate } from '@/lib/personaProtection';
import type { PlanTier } from '@/hooks/useSubscription';

import type { Database } from '@/types/supabase';

export interface Persona {
    id: string;
    name: string;
    occupation: string;
    age: number | string;
    gender: string;
    location: string;
    income: string;
    bio: string;
    worldview: string;
    /** CamelCase field used everywhere in the frontend */
    painPoints: string;
    communication: string;
    triggers: string;
    user_id?: string;
    avatarUrl?: string;
    collectionId?: string | null;
    tags?: { id: string; name: string; color: string }[];
    createdAt?: string;
}

// Shared row-to-Persona mapper — single source of truth
const mapRow = (p: Database['public']['Tables']['personas']['Row'] & {
    tags?: { tag: Database['public']['Tables']['insight_tags']['Row'] }[]
}): Persona => ({
    id: p.id,
    name: p.name || '',
    occupation: p.occupation || '',
    age: p.age ?? 0,
    gender: p.gender || '',
    location: p.location || '',
    income: p.income || '',
    bio: p.bio || '',
    worldview: p.worldview || '',
    painPoints: p.pain_points || '',
    communication: p.communication || '',
    triggers: p.triggers || '',
    user_id: p.user_id,
    avatarUrl: p.avatar_url ?? undefined,
    collectionId: p.collection_id,
    tags: p.tags ? p.tags.map(t => ({
        id: t.tag.id,
        name: t.tag.name || '',
        color: t.tag.color || 'slate'
    })) : [],
    createdAt: p.created_at,
});

export const personaService = {
    async fetchAll(offset: number = 0, limit: number = 50): Promise<Persona[]> {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return [];

        const { data, error } = await supabase.from('personas')
            .select('*, tags:persona_tag_links(tag:insight_tags(*))')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);
            
        if (error) throw error;
        return (data || []).map(mapRow);
    },

    async create(persona: Omit<Persona, 'id'> & { tagIds?: string[] }): Promise<Persona> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const dbPersona = {
            name: persona.name,
            age: Number(persona.age),
            occupation: persona.occupation,
            location: persona.location,
            income: persona.income,
            bio: persona.bio,
            worldview: persona.worldview,
            pain_points: persona.painPoints,
            communication: persona.communication,
            triggers: persona.triggers,
            gender: persona.gender,
            user_id: user.id,
            avatar_url: persona.avatarUrl,
            collection_id: persona.collectionId || null,
        };

        const { data, error } = await supabase.from('personas').insert(dbPersona).select('*, tags:persona_tag_links(tag:insight_tags(*))').single();
        if (error) throw error;
        const newPersona = mapRow(data);

        if (persona.tagIds && persona.tagIds.length > 0) {
            await personaService.updateTags(newPersona.id, persona.tagIds);
            const { data: updated } = await supabase.from('personas').select('*, tags:persona_tag_links(tag:insight_tags(*))').eq('id', newPersona.id).single();
            if (updated) return mapRow(updated);
        }
        return newPersona;
    },

    async update(persona: Persona & { tagIds?: string[] }): Promise<Persona> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Busca dados originais da persona e perfil para validação de segurança
        const [personaRes, profileRes] = await Promise.all([
            supabase.from('personas').select('*').eq('id', persona.id).eq('user_id', user.id).single(),
            supabase.from('profiles').select('subscription_tier, is_special_grant').eq('id', user.id).single()
        ]);

        if (personaRes.data) {
            const originalPersona = mapRow(personaRes.data);
            const tier = profileRes.data?.subscription_tier as PlanTier || 'basic';
            const isSpecialGrant = Boolean(profileRes.data?.is_special_grant);

            const validation = validatePersonaUpdate(originalPersona, persona, tier, isSpecialGrant);
            if (!validation.valid) {
                throw new Error(validation.reason || 'Alteração não permitida pelas diretrizes do seu plano.');
            }
        }

        const dbPersona = {
            name: persona.name,
            age: Number(persona.age),
            occupation: persona.occupation,
            location: persona.location,
            income: persona.income,
            bio: persona.bio,
            worldview: persona.worldview,
            pain_points: persona.painPoints,
            communication: persona.communication,
            triggers: persona.triggers,
            gender: persona.gender,
            avatar_url: persona.avatarUrl,
            collection_id: persona.collectionId || null,
        };
        const { data, error } = await supabase.from('personas').update(dbPersona).eq('id', persona.id).eq('user_id', user.id).select('*, tags:persona_tag_links(tag:insight_tags(*))').single();
        if (error) throw error;

        const tagIds = persona.tagIds;
        if (tagIds !== undefined) {
            await personaService.updateTags(persona.id, tagIds);
            const { data: updated } = await supabase.from('personas').select('*, tags:persona_tag_links(tag:insight_tags(*))').eq('id', persona.id).single();
            if (updated) return mapRow(updated);
        }
        return mapRow(data);
    },

    async updateTags(personaId: string, tagIds: string[]): Promise<void> {
        await supabase.from('persona_tag_links').delete().eq('persona_id', personaId);
        if (tagIds.length > 0) {
            const links = tagIds.map(tagId => ({ persona_id: personaId, tag_id: tagId }));
            await supabase.from('persona_tag_links').insert(links);
        }
    },

    async delete(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error } = await supabase.from('personas').delete().eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },

    async generateAvatarInBackground(personaId: string, imagePrompt: string): Promise<void> {
        try {
            const publicUrl = await callGeminiImage(imagePrompt, personaId);
            if (publicUrl) {
                const { error } = await supabase.from('personas').update({ avatar_url: publicUrl }).eq('id', personaId);
                if (error) console.error('Error saving generated avatar URL to database:', error);
            }
        } catch (e) {
            console.error('Background avatar generation failed (fire-and-forget process):', e);
        }
    }
};


